import { useState } from "react";
import { exportAsImage } from "../../utils/exportAsImage";

export default function PlayerPerformance({ results, players }) {
  const [activeTab, setActiveTab] = useState("general");

  // Build statistics for each player
  const stats = players.map((p) => {
    const playerResults = results.filter((r) => r.playerId === p.id);

    const national = playerResults.filter((r) => r.rankingType === "National");
    const ajss = playerResults.filter((r) => r.rankingType === "AJSS");

    const nationalBest = national.length
      ? Math.min(
          ...national.map((r) =>
            typeof r.rank === "number" ? r.rank : Infinity
          )
        )
      : null;

    const ajssBest = ajss.length
      ? Math.min(
          ...ajss.map((r) =>
            typeof r.rank === "number" ? r.rank : Infinity
          )
        )
      : null;

    const totalTournaments =
      new Set(playerResults.map((r) => r.tournamentId)).size;

    const totalMedals = playerResults.filter((r) => r.medal).length;

    const bestRank = Math.min(
      nationalBest ?? Infinity,
      ajssBest ?? Infinity
    );
    const bestRankDisplay = bestRank === Infinity ? null : bestRank;

    return {
      name: p.fullName,
      totalTournaments,
      totalMedals,
      bestRank: bestRankDisplay,
      national: {
        tournaments: new Set(national.map((r) => r.tournamentId)).size,
        medals: national.filter((r) => r.medal).length,
        bestRank: nationalBest === Infinity ? null : nationalBest,
      },
      ajss: {
        tournaments: new Set(ajss.map((r) => r.tournamentId)).size,
        medals: ajss.filter((r) => r.medal).length,
        bestRank: ajssBest === Infinity ? null : ajssBest,
      },
    };
  });

  return (
    <div className="p-4 bg-white shadow rounded-xl">
      <div className="flex justify-between items-center mb-2">
        {/* Tabs */}
        <div className="flex border-b mb-4">
          <button
            className={`px-4 py-2 font-medium ${
              activeTab === "general"
                ? "border-b-2 border-blue-600 text-blue-600"
                : ""
            }`}
            onClick={() => setActiveTab("general")}
          >
            General
          </button>
          <button
            className={`px-4 py-2 font-medium ${
              activeTab === "detailed"
                ? "border-b-2 border-blue-600 text-blue-600"
                : ""
            }`}
            onClick={() => setActiveTab("detailed")}
          >
            Detailed
          </button>
        </div>

        {/* Save button */}
        <button
          onClick={() =>
            exportAsImage(
              activeTab === "general"
                ? "player-performance-general"
                : "player-performance-detailed",
              `player_performance_${activeTab}`
            )
          }
          className="px-1 py-1 bg-blue-500 text-white rounded"
        >
          Save as Image
        </button>
      </div>

      {/* GENERAL TABLE */}
      {activeTab === "general" && (
        <div id="player-performance-general" className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300 text-center text-sm min-w-[400px]">
            <thead className="bg-gray-100">
              <tr>
                <th className="border border-gray-300 p-2 text-left sticky left-0 bg-white">
                  Player
                </th>
                <th className="border border-gray-300 p-2">Tournaments</th>
                <th className="border border-gray-300 p-2">Medals</th>
                <th className="border border-gray-300 p-2">
                  Best Tournament Result
                </th>
              </tr>
            </thead>
            <tbody>
              {stats.map((s) => (
                <tr key={s.name}>
                  <td className="border border-gray-300 p-2 text-left sticky left-0 bg-white">
                    {s.name}
                  </td>
                  <td className="border border-gray-300 p-2">
                    {s.totalTournaments}
                  </td>
                  <td className="border border-gray-300 p-2">
                    {s.totalMedals}
                  </td>
                  <td className="border border-gray-300 p-2">
                    {s.bestRank ? `#${s.bestRank}` : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* DETAILED TABLE */}
      {activeTab === "detailed" && (
        <div id="player-performance-detailed" className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300 text-center text-sm min-w-[800px]">
            <thead className="bg-gray-100">
              <tr>
                <th className="border border-gray-300 p-2 text-left sticky left-0 bg-white">
                  Player
                </th>
                <th className="border border-gray-300 p-2">
                  No. Tournaments (National)
                </th>
                <th className="border border-gray-300 p-2">
                  No. Medals (National)
                </th>
                <th className="border border-gray-300 p-2">
                  Best Rank (National)
                </th>
                <th className="border border-gray-300 p-2">
                  No. Tournaments (AJSS)
                </th>
                <th className="border border-gray-300 p-2">
                  No. Medals (AJSS)
                </th>
                <th className="border border-gray-300 p-2">
                  Best Rank (AJSS)
                </th>
              </tr>
            </thead>
            <tbody>
              {stats.map((s) => (
                <tr key={s.name}>
                  <td className="border border-gray-300 p-2 text-left sticky left-0 bg-white">
                    {s.name}
                  </td>
                  <td className="border border-gray-300 p-2">
                    {s.national.tournaments}
                  </td>
                  <td className="border border-gray-300 p-2">
                    {s.national.medals}
                  </td>
                  <td className="border border-gray-300 p-2">
                    {s.national.bestRank ? `#${s.national.bestRank}` : "-"}
                  </td>
                  <td className="border border-gray-300 p-2">
                    {s.ajss.tournaments}
                  </td>
                  <td className="border border-gray-300 p-2">
                    {s.ajss.medals}
                  </td>
                  <td className="border border-gray-300 p-2">
                    {s.ajss.bestRank ? `#${s.ajss.bestRank}` : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
